import { IncidentFiltersEnrichmentQuery } from '@/graphql/generated/operations'
import {
  FacilityOptionWithAddressI,
  OptionI,
  Options,
} from '@/utils/forms/optionBuilders'

import { IncidentFiltersFormInputsI } from '../types/form'

export const removeEmptyFilters = (values: IncidentFiltersFormInputsI) => {
  return Object.keys(values).forEach((key) => {
    // Delete null values and empty arrays
    if (values[key] === null || values[key]?.length == 0) {
      delete values[key]
    }
  })
}

export const compareAlphabetically = (
  a: { name: string },
  b: { name: string }
) => (a.name.toLocaleLowerCase() < b.name.toLocaleLowerCase() ? -1 : 1)

// idIn: [] is treated by the backend as "no filter" and returns everything —
// a nonexistent sentinel id keeps the enrichment query scoped to genuinely
// pre-selected devices/facilities instead. Shared by both, since the same
// footgun and the same fix apply to either id type.
export const dummyEntityId = '00000000-0000-0000-0000-000000000000'

export const areAllDefined = (...entities: (object | undefined)[]): boolean => {
  return entities.every((entity) => entity !== undefined)
}

export const hasFacilitiesOrFloors = (
  watchFacility: OptionI[] | undefined,
  watchFloor: OptionI[] | undefined
): boolean => watchFacility?.length > 0 || watchFloor?.length > 0

/**
 * Builds facility options from the enrichment query in the same
 * `{ label, value, subtitle }` shape getIncidentFacilityOptionsWithAddress
 * produces for search results, so a pre-selected facility renders
 * consistently with the rest of the (subtitle-based) browse-mode list
 * instead of the older inline "Name - (SN)" label format. The enrichment
 * query doesn't fetch `address` (see incidentFiltersEnrichment.gql), so the
 * subtitle here is short-name only — still consistent in *format*, just
 * missing the address line a real search result could have.
 */
export const getEnrichmentFacilityOptions = (
  enrichmentData: IncidentFiltersEnrichmentQuery
): FacilityOptionWithAddressI[] =>
  (enrichmentData?.facilityNames?.edges ?? []).map((edge) => ({
    label: `${edge?.node?.name}${edge?.node?.archived ? ' - ARCHIVED' : ''}`,
    value: edge?.node?.id,
    subtitle: edge?.node?.shortName || undefined,
  }))

/**
 * Merges pre-selected facilities with initial facility options.
 * This ensures pre-selected facilities always appear in the dropdown,
 * even if they're not in the initial 100 facilities fetched.
 *
 * `initialFacilities` (the real browse/search page, built by
 * getIncidentFacilityOptionsWithAddress) is added first and wins on
 * conflict: it always carries a subtitle with short name AND address, while
 * `preSelectedFacilities` (built by getEnrichmentFacilityOptions) can only
 * ever have short name — that query doesn't fetch `address`. Only add a
 * pre-selected facility when the page doesn't already have it, which is what
 * actually guarantees it never disappears from the list.
 */
export const getMergedFacilityOptions = (
  preSelectedFacilities: Options,
  initialFacilities: Options | undefined
): Options => {
  const baseFacilities = initialFacilities ?? []

  // Create a map to deduplicate by facility ID
  const facilityMap = new Map<string, OptionI>()

  baseFacilities.forEach((facility) => {
    if (facility.value) {
      facilityMap.set(facility.value, facility)
    }
  })

  // Add pre-selected facilities only for ids the page doesn't already have.
  preSelectedFacilities.forEach((facility) => {
    if (facility.value && !facilityMap.has(facility.value)) {
      facilityMap.set(facility.value, facility)
    }
  })

  return Array.from(facilityMap.values())
}
